import { useRef, useCallback, useMemo, useEffect } from 'react';
import { DocumentNode } from 'graphql';
import { useApolloClient } from '@common/hooks';

const DEFAULT_DELAY_MS = 150;

type UsePrefetchQueryOptionsType = {
  query: DocumentNode;
  variables?: Record<string, unknown>;
};

export function usePrefetchQuery({
  query,
  variables,
}: UsePrefetchQueryOptionsType) {
  const client = useApolloClient();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stableVariables = useMemo(
    () => variables,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(variables)]
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const onMouseEnter = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      void client
        .query({
          query,
          variables: stableVariables,
          fetchPolicy: 'cache-first',
        })
        .catch(() => {});
      timerRef.current = null;
    }, DEFAULT_DELAY_MS);
  }, [client, query, stableVariables]);

  const onMouseLeave = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return { prefetchHandlers: { onMouseEnter, onMouseLeave } };
}
